import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { createOrderNote, getOrderNotes } from "src/queries/query";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// Helper function to format date for display
const formatMessageDate = (dateString) => {
  const date = parseISO(dateString);

  if (isToday(date)) {
    return "TODAY";
  } else if (isYesterday(date)) {
    return "YESTERDAY";
  } else {
    return format(date, "MM/dd/yyyy");
  }
};

// Helper function to format time for display
const formatMessageTime = (dateString) => {
  const date = parseISO(dateString);
  return format(date, "HH:mm");
};

export default function MixedActivityView({
  order,
  manifestUuid,
  isManifest = false,
}) {
  const [inputText, setInputText] = useState("");
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const orderId = order?.uuid || order?.id;
  const finalManifestUuid = manifestUuid || order?.manifestUuid;

  // Fetch order notes
  const {
    data: orderNotesData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["order-notes", orderId, finalManifestUuid],
    queryFn: () =>
      getOrderNotes(
        isManifest ? null : orderId,
        isManifest ? finalManifestUuid : null,
      ),
    enabled: !!(isManifest ? finalManifestUuid : orderId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const orderNotes = orderNotesData?.data || [];

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (payload) => createOrderNote(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["order-notes", orderId, finalManifestUuid],
      });
      setInputText("");
      setError(null);
    },
    onError: (err) => {
      setError(err.response?.data?.message || "Failed to create note");
      console.error("Error creating note:", err);
    },
  });

  // Handle query errors
  React.useEffect(() => {
    if (queryError) {
      setError(
        queryError.response?.data?.message || "Failed to load order notes",
      );
    }
  }, [queryError]);

  // Combine notes and events
  const combinedItems = React.useMemo(() => {
    const items = [];

    // Add order notes
    orderNotes.forEach((note) => {
      items.push({
        type: "note",
        data: note,
        date: note.createdAt,
      });
    });

    // Add events
    order?.events?.forEach((event) => {
      items.push({
        type: "event",
        data: event,
        date: event.createdAt,
      });
    });

    // Sort by date (newest first)
    items.sort((a, b) => new Date(b.date) - new Date(a.date));

    return items;
  }, [orderNotes, order?.events]);

  // Group combined items by date
  const groupedItems = React.useMemo(() => {
    const grouped = {};

    combinedItems.forEach((item) => {
      const dateKey = formatMessageDate(item.date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(item);
    });

    // Sort dates (ascending - oldest first)
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === "Today") return 1;
        if (b === "Today") return -1;
        if (a === "Yesterday") return 1;
        if (b === "Yesterday") return -1;
        const dateA = new Date(grouped[a][0]?.date);
        const dateB = new Date(grouped[b][0]?.date);
        return dateA - dateB;
      })
      .reduce((result, key) => {
        // Sort items within each date (ascending - oldest first)
        result[key] = grouped[key].sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );
        return result;
      }, {});
  }, [combinedItems]);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when items change
  useEffect(() => {
    scrollToBottom();
  }, [groupedItems]);
  const handleSendNote = () => {
    if (!inputText.trim() || createNoteMutation.isPending) return;

    const noteText = inputText.trim();
    setError(null);

    const payload = {
      note: noteText,
    };

    if (isManifest) {
      payload.manifestId = finalManifestUuid;
      payload.orderId = null;
    } else {
      payload.orderId = orderId;
      payload.manifestId = null;
    }

    console.log("Sending note payload:", payload);

    createNoteMutation.mutate(payload);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendNote();
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
      }}
    >
      {/* Scrollable content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: { xs: 1.5, sm: 2 },
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "3px",
            "&:hover": {
              background: "#a8a8a8",
            },
          },
        }}
      >
        {/* Error Message */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              backgroundColor: "#fee",
              border: "1px solid #fcc",
              borderRadius: 1,
              borderLeft: "3px solid #f44",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#c33", fontSize: "0.75rem" }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* Legacy Shipper/Consignee Notes */}
        {(order?.shipperNotes || order?.consigneeNotes) && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Order Information
            </Typography>
            {order?.shipperNotes && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Shipper Notes:
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {order.shipperNotes}
                </Typography>
              </Box>
            )}
            {order?.consigneeNotes && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Consignee Notes:
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {order.consigneeNotes}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Billing Party Notes */}
        {order?.billingParty?.notes && (
          <Box
            sx={{
              backgroundColor: "#FAF9EF",
              border: "1px solid #f0e68c",
              borderRadius: 2,
              p: { xs: 1.5, sm: 2 },
              mb: 3,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: "#8b7d3a",
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              Billing Party Notes
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#b5b3a1",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
              }}
            >
              {order.billingParty.notes}
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#1e55bf" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading activity...
            </Typography>
          </Box>
        ) : Object.keys(groupedItems).length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontWeight: 500,
              }}
            >
              No activity yet
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Notes and events will appear here
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedItems).map(([date, dateItems]) => (
            <Box key={date} sx={{ mb: 2 }}>
              <Box
                sx={{
                  textAlign: "center",
                  mb: 1,
                  position: "sticky",
                  top: -20,
                  zIndex: 1,
                  backgroundColor: "white",
                  py: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#1e55bf",
                    backgroundColor: "#e3f2fd",
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                    display: "inline-block",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    border: "1px solid #bbdefb",
                  }}
                >
                  {date}
                </Typography>
              </Box>

              {dateItems.map((item, index) => {
                if (item.type === "note") {
                  const note = item.data;
                  const fullName = note.user
                    ? `${note.user.firstName} ${note.user.lastName}`
                    : note.createdBy || "System";
                  return (
                    <Box
                      key={`note-${note.id}`}
                      sx={{
                        display: "flex",
                        mb: 2.5,
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        backgroundColor: "#fff3e0",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: "#ffe0b2",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          backgroundColor: "#1e55bf",
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            lineHeight: 1.6,
                            mb: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              mr: 1,
                            }}
                          >
                            {fullName}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {note.note}
                          </Box>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box component="span">
                            {formatMessageTime(note.createdAt)}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  );
                } else {
                  const event = item.data;
                  const fullName = event.user
                    ? `${event.user.firstName} ${event.user.lastName}`
                    : "System";

                  return (
                    <Box
                      key={`event-${event.uuid}`}
                      sx={{
                        display: "flex",
                        mb: 2.5,
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "4px",
                          height: "4px",
                          borderRadius: "50%",
                          backgroundColor: "#1e55bf",
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            lineHeight: 1.6,
                            mb: 0.5,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              mr: 1,
                            }}
                          >
                            {fullName}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            {event.text.substring(fullName.length + 1)}
                          </Box>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.65rem", sm: "0.7rem" },
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Box component="span">
                            {formatMessageTime(event.createdAt)}
                          </Box>
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Fixed bottom input */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "#eeeeee99",
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <Typography
          fontWeight="bold"
          color="primary"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          🗒️ Create {isManifest ? "Manifest" : "Order"} Note
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <TextField
            fullWidth
            multiline
            size="small"
            maxRows={3}
            variant="outlined"
            placeholder="Type a note..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={createNoteMutation.isPending}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                backgroundColor: "#f8f9fa",
                fontSize: "0.875rem",
                "& fieldset": {
                  borderColor: "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: "#bdbdbd",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1e55bf",
                  borderWidth: "1.5px",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendNote}
            disabled={!inputText.trim() || createNoteMutation.isPending}
            sx={{
              backgroundColor: "#1e55bf",
              color: "white",
              width: 40,
              height: 40,
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#1648a3",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {createNoteMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon sx={{ fontSize: "1.1rem" }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
